import cx from 'classnames';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {ORIGIN_DICT} from '~/common/constants/event-origins';
import {useContentHeight} from '~/common/fabric/Card/components/AnimationPreview/hooks/useContentHeight';
import {useInviteName} from '~/common/fabric/Card/components/AnimationPreview/hooks/useInviteName';
import {MainAssetSlide} from '~/common/fabric/Card/components/AnimationPreview/MainAssetSlide';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {TriangleIcon} from '~library/svg/TriangleIcon';
import {largishAndUpQuery} from '~libSass/base/_exports.sass';
import {webviewAnimFinish, webviewAnimReplay, webviewAnimStart} from './webviewMessages';

export const PremiumCarousel = ({
  children,
  isWebview,
  recipientName,
  height,
  replayOffsetWidth,
  animationStartCallback,
  animationEndCallback,
}) => {
  const [replayOffset, setReplayOffset] = useState(0);
  const [firstRender, setFirstRender] = useState(true);

  const envelope = useSelector((state) => state.premiumDesign.envelope);
  const stamp = useSelector((state) => state.premiumDesign.stamp);
  const liner = useSelector((state) => state.premiumDesign.liner);
  const guestName = useSelector((state) => state.guest?.name || state.user?.userName);
  const origin = useSelector((state) => state.eventDetails.origin);
  const envelopeStickers = useSelector((state) => state.premiumDesign.envelopeStickers);
  const inviteName = recipientName || useInviteName({guestName, origin});

  const alreadyRanAnimation = useRef(false);

  const {matches: largeScreens} = useMatchQuery(`(${largishAndUpQuery})`);

  const inviteContainerRef = useRef();
  const desktopInviteRef = useRef();
  const replayButtonRef = useRef();
  const stickerContainerRef = useRef();
  const envelopeWidthRef = useRef();

  const containerHeight = useContentHeight({
    containerHeight: height,
    isWebview,
  });

  const animationFinished = useCallback(() => {
    setReplayOffset(replayOffsetWidth || 0);
    replayButtonRef?.current?.classList.remove('hidden');
    stickerContainerRef?.current?.classList.remove('hidden');

    animationEndCallback?.(!alreadyRanAnimation.current);

    alreadyRanAnimation.current = true;

    webviewAnimFinish();
  }, [animationEndCallback, replayOffsetWidth]);

  const envelopeAnimation = useCallback(() => {
    inviteContainerRef?.current?.classList.remove('invite-container');
    inviteContainerRef?.current?.classList.add('invite-container');

    webviewAnimStart();
    animationStartCallback?.(!alreadyRanAnimation.current);

    inviteContainerRef?.current?.classList.remove('step-0');
    inviteContainerRef?.current?.classList.add('step-1');

    window.setTimeout(() => {
      stickerContainerRef?.current?.classList.add('hidden');
    }, 300);

    window.setTimeout(() => {
      inviteContainerRef?.current?.classList.remove('step-1');
      inviteContainerRef?.current?.classList.add('step-2');

      window.setTimeout(() => {
        inviteContainerRef?.current?.classList.remove('step-2');
        inviteContainerRef?.current?.classList.add('step-3');

        window.setTimeout(() => {
          inviteContainerRef?.current?.classList.remove('step-3');
          inviteContainerRef?.current?.classList.add('step-4');

          window.setTimeout(() => {
            inviteContainerRef?.current?.classList.remove('step-4');
            inviteContainerRef?.current?.classList.add('step-5');

            window.setTimeout(() => {
              animationFinished();
            }, 1000);
          }, 750);
        }, 250);
      }, 500);
    }, 1000);
  }, [animationFinished, animationStartCallback]);

  const replayAnimation = useCallback(() => {
    replayButtonRef?.current?.classList.add('hidden');

    inviteContainerRef?.current?.classList.add('reset');
    webviewAnimReplay();

    window.setTimeout(() => {
      inviteContainerRef?.current?.classList.remove('reset');
      inviteContainerRef?.current?.classList.add('step-0');
      envelopeAnimation();
    }, 1000);
  }, [envelopeAnimation]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (!firstRender && !alreadyRanAnimation.current) {
      window.setTimeout(() => {
        envelopeAnimation();
      }, 2000);
    }
  }, [firstRender, alreadyRanAnimation.current]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setFirstRender(false);

    // if app webview, set replayAnimation function to be called by the app
    if (isWebview) {
      window.replayAnimation = replayAnimation;
    }
  }, []);

  // TODO: Split carousel logic to a reusable component
  return (
    <div className="premium-carousel__container">
      <div className="premium-carousel">
        <div
          className={cx(`premium-carousel__page`, {
            fabric: origin === ORIGIN_DICT.FABRICGREETINGCARD,
          })}
        >
          <div className="premium-design__container">
            <MainAssetSlide
              isWebview={isWebview}
              envelope={envelope}
              stamp={stamp}
              liner={liner}
              inviteContainerRef={inviteContainerRef}
              desktopInviteRef={desktopInviteRef}
              envelopeWidthRef={envelopeWidthRef}
              stickerContainerRef={stickerContainerRef}
              envelopeStickers={envelopeStickers}
              containerHeight={containerHeight}
              inviteName={inviteName}
            >
              {children}
            </MainAssetSlide>
          </div>
        </div>
      </div>
      {!isWebview && (
        <button
          className="animation-replay-button hidden"
          ref={replayButtonRef}
          onClick={replayAnimation}
          data-qa-id="replay-animation"
          type="button"
          style={
            largeScreens
              ? {
                  right: `max(calc(${
                    (desktopInviteRef.current?.offsetWidth || 0) * 0.5
                  }px - 5.6125rem - ${replayOffset / 2}px - 2.5rem), 3rem)`,
                  top: `calc(50% + 1rem)`,
                }
              : {
                  bottom: `-1rem`,
                  right: `1rem`,
                }
          }
        >
          <TriangleIcon ratio={1.1} />
          Replay
        </button>
      )}
    </div>
  );
};
